import React, { useContext, useEffect, useRef, useState } from 'react'
import './effects.css'
import { ProjectContext } from '../../../context/ProjectContext'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { SelectBar } from '../selectBar'
import { createPortal } from 'react-dom'
import { EditDialog } from '../../dialogs/editDialog'
import cnt from '../../../constants'
import { useDebounce } from '../../../hooks/useDebounce'
import useOutsideClick from '../../../hooks/useOutsideClick'

export const Note = ({ 
  note, 
  selected, 
  handleNoteSelect, 
  noteHover, 
  setNoteHover, 
  onDeselect = () => {},
  noteRef,
  allNoteRefs,
  isLastSelected
}) => {
  const {
    meetings,
    newNoteId,
    setNotes,
    categories,
    setCategories,
    setScrollToEnd,
    project,
    setProject,
    participants,
  } = useContext(ProjectContext)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [noteContent, setNoteContent] = useState('')
  const [showPercentage, setShowPercentage] = useState(false)
  const debouncedNoteContent = useDebounce(noteContent, 1000)
  const divRef = useRef(null)
  const textRef = useRef(null)
  const [smallFontSize, setSmallFontSize] = useState(false)
  const [truncateText, setTruncateText] = useState(0)
  const [truncateHover, setTruncateHover] = useState(false)
  const customFetch = useCustomFetch()
  const selectBarRef = useRef(null)

  // Get all note refs except the current note
  const otherNoteRefs = Object.entries(allNoteRefs)
    .filter(([id]) => id !== note.id)
    .map(([, ref]) => ref)

  useOutsideClick([divRef, selectBarRef], () => {
    if (selected) {
      onDeselect()
    }
  }, otherNoteRefs)

  useEffect(() => {
    if (textRef.current) {
      // Wait a bit for layout to stabilize
      const timeoutId = setTimeout(() => {
        // Get the computed styles for the <p> element
        let styles = window.getComputedStyle(textRef.current)
        let lineHeight = parseFloat(styles.lineHeight)
        let height = textRef.current.offsetHeight
        let lines = Math.ceil(height / lineHeight)
        
        // Truncate after 5 lines
        if (lines <= 5) {
          // Text fits without truncation
          setSmallFontSize(false)
          setTruncateText(0)
        } else {
          // Text needs truncation
          setSmallFontSize(true)
          
          // Calculate how many characters we can show
          textRef.current.style.fontSize = '12px' // Apply smaller font
          styles = window.getComputedStyle(textRef.current)
          lineHeight = parseFloat(styles.lineHeight)
          
          let amountChars = note.content.length
          textRef.current.innerText = note.content
          height = textRef.current.offsetHeight
          lines = Math.ceil(height / lineHeight)
          
          if (lines > 5) {
            while (lines > 5 && amountChars > 0) {
              amountChars -= 3
              textRef.current.innerText = note.content.slice(0, amountChars)
              height = textRef.current.offsetHeight
              lines = Math.ceil(height / lineHeight)
            }
            setTruncateText(amountChars - 3)
          } else {
            // With the smaller font, it fits without truncation
            setTruncateText(0)
          }
        }
      }, 50); // small delay to ensure DOM is ready
      
      return () => clearTimeout(timeoutId);
    }
  }, [note.content])

  useEffect(() => {
    if (newNoteId === note.id) {
      setScrollToEnd(note.meetingId)
    }
  }, [newNoteId])

  useEffect(() => {
    if (!noteContent) return
    const handleChangeNoteContent = async (content) => {
      try {
        setNotes((prevNotes) => prevNotes.map((n) => (n.id === note.id ? { ...n, content } : n)))
        await customFetch('/updateNoteContent', 'PUT', { noteId: note.id, content })
      } catch (error) {
        console.log(error)
        toast.error('Whoops! Something went wrong. Please try again.')
      }
    }
    handleChangeNoteContent(noteContent)
  }, [debouncedNoteContent])

  const handleChangeNoteCategory = async (categoryId) => {
    try {
      // if (categoryId !== 'Uncategorized')
      if (!note.category_id) note.category_id = 'Uncategorized'
      const newCategories = JSON.parse(JSON.stringify(categories))
      const prevCatIndex = newCategories.findIndex((c) => c.id === note.category_id)
      const newCatIndex = newCategories.findIndex((c) => c.id === categoryId)
      newCategories[prevCatIndex].amount--
      newCategories[newCatIndex].amount++
      setCategories(newCategories)
      setNotes((prevNotes) =>
        prevNotes.map((n) =>
          n.id === note.id
            ? {
                ...n,
                category_id: categoryId === 'Uncategorized' ? undefined : categoryId,
              }
            : n
        )
      )
      await customFetch('/updateNoteCategory', 'PUT', { notes: [note.id], categoryId })
    } catch (error) {
      console.log(error)
      toast.error('Whoops! Something went wrong. Please try again.')
    }
  }

  const handleChangeNoteColor = async (color) => {
    try {
      setNotes((prevNotes) => prevNotes.map((n) => (n.id === note.id ? { ...n, color } : n)))
      await customFetch('/updateNoteColor', 'PUT', { notes: [note.id], color })
    } catch (error) {
      console.log(error)
      toast.error('Whoops! Something went wrong. Please try again.')
    }
  }

  const handleDeleteNote = async () => {
    try {
      setNotes((prevNotes) => prevNotes.filter((n) => n.id !== note.id))
      setShowEditDialog(false)
      toast.success('Note deleted successfully')
      await customFetch('/deleteNotes', 'DELETE', { notes: [note.id], projectId: note.projectId })
    } catch (error) {
      console.log(error)
      toast.error('Whoops! Something went wrong. Please try again.')
    }
  }

  return (
    <div className="m-2 relative" ref={noteRef}>
      {/* Cluster badge positioned absolutely in top-right corner */}
      {Array.isArray(note.clustered_notes) && note.clustered_notes.length > 0 && note.meetings_count > 1 && (
        <div 
          className="absolute -top-2 -right-2"
          onMouseEnter={() => setShowPercentage(true)}
          onMouseLeave={() => setShowPercentage(false)}
        >
          <div className={`relative transition-all duration-200 ease-in-out ${showPercentage ? 'w-[48px]' : 'w-[32px]'} h-[32px]`}>
            <svg 
              width="32" 
              height="32" 
              viewBox="0 0 32 32" 
              className={`absolute transition-opacity duration-200 ${showPercentage ? 'opacity-0' : 'opacity-100'}`}
            >
              <circle
                cx="16"
                cy="16"
                r="13"
                fill="black"
                stroke="#333333"
                strokeWidth="3"
              />
              <circle
                cx="16"
                cy="16"
                r="13"
                fill="none"
                stroke="#FF4A8E"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${(Math.round((note.meetings_count * 100) / meetings.length) / 100) * 81.68} 81.68`}
                transform="rotate(-90 16 16)"
              />
            </svg>
            <div className={`absolute inset-0 flex items-center justify-center ${showPercentage ? 'bg-black rounded-full px-2' : ''}`}>
              <span className="text-white text-sm font-bold font-['Manrope'] leading-none transition-all duration-200 whitespace-nowrap">
                {showPercentage 
                  ? `${Math.round((note.meetings_count * 100) / meetings.length)}%`
                  : note.meetings_count
                }
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Existing note content */}
      <div
        ref={divRef}
        style={{ backgroundColor: note.color || cnt.default_notes_color }}
        className={`flex flex-col justify-between w-[210px] h-[134px] p-5 rounded shadow ${newNoteId === note.id && ' colorFade'} ${truncateText && 'transition-transform duration-300 hover:w-[230px] hover:h-[154px] hover:-m-[10px]'}  ${selected && 'border-2 border-pink rounded-[6px]'}`}
        onClick={(e) => handleNoteSelect(note.id, e)}
        onMouseEnter={() => {
          setNoteHover(note.id)
          truncateText && setTruncateHover(true)
        }}
        onMouseLeave={() => {
          setNoteHover(null)
          truncateText && setTruncateHover(false)
        }}
        onDoubleClick={() => setShowEditDialog(true)}
      >
        <p
          ref={textRef}
          className={`text-black font-semibold font-['Manrope'] ${smallFontSize ? 'text-xs' : 'text-sm'}`}
        >
          {truncateHover ? note.content : (truncateText ? note.content.slice(0, truncateText) + '...' : note.content)}
        </p>
      </div>

      {/* SelectBar */}
      {selected && isLastSelected && (
        <SelectBar
          ref={selectBarRef}
          note={note}
          noteOrFeedback="note"
        />
      )}

      {/* Edit Dialog */}
      {showEditDialog &&
        createPortal(
          <EditDialog
            entity={note}
            project={project}
            participants={participants}
            setProject={setProject}
            setEntities={setNotes}
            entityName={'note'}
            meeting={meetings.find((meet) => meet.id === note.meetingId) || {}}
            close={() => setShowEditDialog(false)}
            changeContent={(content) => setNoteContent(content)}
            deleteEntity={handleDeleteNote}
            categories={categories}
            changeCategory={handleChangeNoteCategory}
            changeColor={handleChangeNoteColor}
          />,
          document.body
        )}
    </div>
  )
}
